import {FormProvider, useForm, type UseFormReturn} from "react-hook-form";
import {CheckoutFormFormValues} from "../../../../../components/checkout-form/form/types";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Input, message, Row, Space, Tooltip} from "antd";
import {StyledServiceButtonWrapper} from "../../../../../styles/onboarding/onboarding";
import {useCreateStoreServiceContext} from "../../context/create-service";
import {useCreateEditStoreServiceHook} from "../../hooks/create-service";
import {CreateStoreServiceFormValues} from "../../reducers/create-service/types";
import {offeringCheckoutFormValidationSchema} from "../../../../../components/offering-form/general/checkout-form/validation-schema";
import {OfferingFormCheckoutFormValues} from "../../../../../components/offering-form/general/checkout-form/types";
import {ServiceCheckoutFormFields} from "../../../../../components/offering-form/services/checkout-form";
import {CustomModal} from "../../../../../components/modal";
import {useNavigate} from "react-router-dom";
import {REBRAND_BASE_URL} from "../../../../../services/config";
import {useGetProfileContext} from "../../../../../context/profile/profile-context";
import {StyledModalContentWrapper} from "../../../../../styles/transactions/transactions";
import {StyledSuccessServiceContent} from "../../../../../components/calls/service/service-form/service-form.style";
import {
  CopyOutlined,
  FacebookFilled,
  MailFilled,
  TwitterOutlined,
} from "@ant-design/icons";
import {handleStoreServiceLink} from "../../../../../utils/products";
import {StoreServiceContentUploadConsentFormField} from "../consent";
import {
  OFFERING_CREATE_DRAFT_KEYS,
  useClearOfferingCreateDraft,
  useSyncCheckoutFormToContext,
} from "../../../../../utils/offering-create-draft";

export function CreateServiceCheckoutFormStep() {
  const navigate = useNavigate();
  const {state: profileState} = useGetProfileContext();
  const {
    state: {formData, apiResponseData},
    dispatch,
  } = useCreateStoreServiceContext();
  const checkoutFormMethods = useForm<
    OfferingFormCheckoutFormValues & {consent: boolean}
  >({
    resolver: yupResolver(offeringCheckoutFormValidationSchema),
    mode: "onChange",
    defaultValues: {...formData, consent: false},
  });

  useSyncCheckoutFormToContext({
    checkoutFormMethods:
      checkoutFormMethods as unknown as UseFormReturn<OfferingFormCheckoutFormValues>,
    dispatch,
    saveActionType: "save-create-store-service-form-values",
    formData: {
      bookingQuestions: formData.bookingQuestions ?? [],
    },
  });

  const clearStoreServiceDraft = useClearOfferingCreateDraft(
    OFFERING_CREATE_DRAFT_KEYS.storeService
  );

  const {createEditStoreService, isCreateServiceEditing, wasStoreServiceCreateEdited} =
    useCreateEditStoreServiceHook();

  function onSubmit(values: CheckoutFormFormValues) {
    if (apiResponseData) {
      const payload: CreateStoreServiceFormValues = {
        ...formData,
        ...values,
      };
      createEditStoreService(payload, () => {});
    } else {
      message.error("No service id found. Go back to step 1");
    }
  }

  const isFormError = Object.keys(checkoutFormMethods.formState.errors).length > 0;
  const isBookingQuestionsError = checkoutFormMethods.formState.errors.bookingQuestions;

  const handleSuccessModalOk = () => {
    clearStoreServiceDraft();
    navigate("/products?activeTab=services");
  };

  console.log({apiResponseData});

  const storeServiceBookingLink = handleStoreServiceLink({
    serviceId: apiResponseData?.productService?.id ?? "",
    slug: apiResponseData?.productService?.slug ?? "",
    username: profileState.profileData.username,
    tinyUrl: apiResponseData?.productService?.tiny_url ?? "",
    windowNav: true,
  });

  return (
    <>
      <FormProvider {...checkoutFormMethods}>
        <form onSubmit={checkoutFormMethods.handleSubmit(onSubmit)} autoComplete="off">
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <ServiceCheckoutFormFields
              isBookingQuestionsError={!!isBookingQuestionsError}
            />
            <StoreServiceContentUploadConsentFormField />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    onClick={() => {
                      dispatch({
                        type: "save-create-store-service-form-values",
                        payload: checkoutFormMethods.getValues(),
                      });
                      dispatch({
                        type: "update-create-service-form-step",
                        payload: "automations",
                      });
                    }}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    size="small"
                    disabled={
                      Object.keys(checkoutFormMethods.formState.errors).length > 0 ||
                      Object.values(checkoutFormMethods.watch()).length < 1 ||
                      checkoutFormMethods.watch("consent") === false
                    }
                    loading={isCreateServiceEditing}
                  >
                    Submit
                  </Button>
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
      {wasStoreServiceCreateEdited && (
        <CustomModal
          visibleState={true}
          title="Service created"
          okFunc={handleSuccessModalOk}
          cancelFunc={handleSuccessModalOk}
          footerContent={[
            <Button
              key="publicProfile"
              type="primary"
              href={`${REBRAND_BASE_URL}/${profileState.profileData.username}`}
              target="_blank"
              size="small"
            >
              View my public profile
            </Button>,
            <Button
              size="small"
              key="gotIt"
              type="default"
              onClick={handleSuccessModalOk}
            >
              Got it!
            </Button>,
          ]}
        >
          <StyledModalContentWrapper>
            <StyledSuccessServiceContent>
              <p className="success-text">
                Your service has been successfully created! Share your service link to
                start making sales.
              </p>
              <div className="success-share-info">
                <Input.Group compact style={{display: "flex"}}>
                  <Input size="large" readOnly defaultValue={storeServiceBookingLink} />
                  <Tooltip title={"Copy product url"}>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() =>
                        navigator.clipboard
                          .writeText(storeServiceBookingLink)
                          .then(() => message.success("Copied"))
                      }
                    />
                  </Tooltip>
                </Input.Group>
                <p>or</p>
                <Space direction="horizontal" size={16}>
                  <div>
                    <Button
                      shape="circle"
                      size="small"
                      icon={<FacebookFilled />}
                      style={{
                        backgroundColor: "#EBEEF5",
                        color: "#3B5998",
                        border: "none",
                      }}
                      type="link"
                      target="_blank"
                      href={`https://www.facebook.com/sharer/sharer.php?u=${storeServiceBookingLink}&amp`}
                      title="Share on facebook"
                    />
                  </div>
                  <div>
                    <Button
                      title="Share on twitter"
                      shape="circle"
                      size="small"
                      icon={<TwitterOutlined />}
                      target="_blank"
                      style={{
                        backgroundColor: "#E6F7FD",
                        color: "#00ACEE",
                        border: "none",
                      }}
                      href={`https://twitter.com/intent/tweet?text=I just created a service titled "${apiResponseData?.productService?.name}" on Coachli. Purchase my service on Coachli!&url=${storeServiceBookingLink}&hashtags=coachli,useCoachli`}
                    />
                  </div>
                  <div>
                    <Button
                      title="Send mail"
                      shape="circle"
                      size="small"
                      icon={<MailFilled />}
                      style={{
                        backgroundColor: "#FBECEB",
                        color: "#DB4437",
                        border: "none",
                      }}
                      href={`mailto:?subject=I wanted you to see this site&amp;body=I created a service ${storeServiceBookingLink}`}
                    />
                  </div>
                </Space>
              </div>
            </StyledSuccessServiceContent>
          </StyledModalContentWrapper>
        </CustomModal>
      )}
    </>
  );
}
